
import React, { useEffect, useState } from 'react'

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'

import LoadingButton from '@mui/lab/LoadingButton'

const initialForm = {
    name: '',
    subject: '',
    html_content: '',
    content_type: 'text',
}

function textToHtml(text) {
    const escapeHtml = (value) => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')

    return text
        .split(/\n{2,}/)
        .map((paragraph) => (
            `<p>${escapeHtml(paragraph)
                .replace(/\r\n|\r|\n/g, '<br>')}</p>`
        ))
        .join('')
}

export default function EmailTemplateDialog({
    open,
    handleClose,
    submit,
    initialData,
}) {
    const [form, setForm] = useState(initialForm)
    const [loading, setLoading] = useState(false)
    const [preview, setPreview] = useState(false)

    const isEditing = Boolean(initialData?.id)

    useEffect(() => {
        if (!open) return

        setForm(initialData
            ? {
                name: initialData.name || '',
                subject: initialData.subject || '',
                html_content: initialData.html_content || '',
                content_type: initialData.content_type || 'html',
            }
            : { ...initialForm }
        )

        setPreview(false)
        setLoading(false)
    }, [open, initialData])

    const handleChange = (field) => (event) => {
        const value = event.target.value

        setForm((previous) => ({
            ...previous,
            [field]: value,
        }))
    }

    const handleContentTypeChange = (_, value) => {
        if (!value) return

        setForm((previous) => ({
            ...previous,
            content_type: value,
        }))

        setPreview(false)
    }

    const handleSubmit = async () => {
        if (
            !form.name.trim() ||
            !form.subject.trim() ||
            !form.html_content.trim()
        ) {
            return
        }

        setLoading(true)

        try {
            await submit(form)
        } catch (error) {
            console.error('Erro ao salvar template:', error)
        } finally {
            setLoading(false)
        }
    }

    const previewHtml = form.content_type === 'text'
        ? textToHtml(form.html_content)
        : form.html_content

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                {isEditing
                    ? 'Editar template de e-mail'
                    : 'Novo template de e-mail'}
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pt={1}
                >
                    <TextField
                        size="small"
                        label="Nome"
                        value={form.name}
                        onChange={handleChange('name')}
                        fullWidth
                        required
                    />

                    <TextField
                        size="small"
                        label="Assunto"
                        value={form.subject}
                        onChange={handleChange('subject')}
                        fullWidth
                        required
                    />

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={1}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                        >
                            <Button
                                size="small"
                                onClick={() => setPreview(!preview)}
                                disabled={!form.html_content.trim()}
                            >
                                {preview
                                    ? 'Voltar à edição'
                                    : 'Visualizar'}
                            </Button>
                        </Box>

                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={form.content_type}
                            onChange={handleContentTypeChange}
                            disabled={loading || preview}
                        >
                            <ToggleButton value="text">
                                Texto
                            </ToggleButton>

                            <ToggleButton value="html">
                                HTML
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {preview ? (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                minHeight: 250,
                                overflow: 'auto',
                                p: 2,
                                bgcolor: '#fff',
                                color: '#222',
                            }}
                        >
                            {form.content_type === 'text' ? (
                                <Typography
                                    component="div"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        fontFamily: 'Arial, sans-serif',
                                    }}
                                >
                                    {form.html_content}
                                </Typography>
                            ) : (
                                <iframe
                                    title="Pré-visualização do e-mail"
                                    srcDoc={previewHtml}
                                    sandbox=""
                                    referrerPolicy="no-referrer"
                                    style={{
                                        width: '100%',
                                        minHeight: 350,
                                        border: 0,
                                        background: '#fff',
                                    }}
                                />
                            )}
                        </Box>
                    ) : (
                        <TextField
                            label={
                                form.content_type === 'text'
                                    ? 'Texto do e-mail'
                                    : 'HTML'
                            }
                            value={form.html_content}
                            onChange={handleChange('html_content')}
                            fullWidth
                            required
                            multiline
                            minRows={12}
                            maxRows={24}
                            placeholder={
                                form.content_type === 'text'
                                    ? 'Olá!\n\nEscreva seu e-mail aqui...'
                                    : '<!DOCTYPE html>\n<html lang="pt-BR">\n<body>\n    <h1>Olá!</h1>\n</body>\n</html>'
                            }
                            slotProps={{
                                htmlInput: {
                                    spellCheck:
                                        form.content_type === 'text',
                                    style: {
                                        fontFamily:
                                            form.content_type === 'html'
                                                ? 'monospace'
                                                : 'inherit',
                                        fontSize: 13,
                                    },
                                },
                            }}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                >
                    Cancelar
                </Button>

                <LoadingButton
                    variant="contained"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={
                        !form.name.trim() ||
                        !form.subject.trim() ||
                        !form.html_content.trim()
                    }
                >
                    {isEditing
                        ? 'Salvar alterações'
                        : 'Criar template'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
